/**
 * Auth API Layer.
 *
 * React Query mutations for authentication operations. These are used
 * by the login page and any component that needs to trigger auth actions.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import type { LoginRequest } from "@/types/user";

/**
 * Mutation hook for user login.
 *
 * On success, stores the JWT and fetches the user profile.
 * On error, throws so the component can display an error message.
 */
export function useLogin() {
  const login = useAuthStore((s) => s.login);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => login(credentials),
    onSuccess: () => {
      // Invalidate any cached queries so they refetch with the new auth state.
      queryClient.invalidateQueries();
    },
  });
}

/**
 * Hook for logging out. Clears state and cached queries.
 */
export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const queryClient = useQueryClient();

  return () => {
    logout();
    queryClient.clear();
  };
}
